import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { apiService } from '../../../services/api';
import { queryKeys } from '../../../lib/queryKeys';
import { readBusinessId } from '../../../lib/auth/tokenClaims';
import {
    CREATE_BUSINESS_COUNTRIES,
    createBusinessSchema,
    type CreateBusinessForm,
} from '../../../schemas/createBusiness.schema';
import sectionStyles from './sections/Section.module.css';
import styles from './GoLive.module.css';

type FieldErrors = Partial<Record<keyof CreateBusinessForm, string>>;

/**
 * AB#2108 (Admin V1 P0-2) — founder-controlled go-live, step 1: an owner with no
 * business creates it as a **Draft** through the existing `PUT /onboarding/draft`.
 * The Draft is not visible to customers; the owner completes the My Business
 * sections and then "Submit to Devanza" for founder review.
 *
 * Name + country + city are collected here because the backend only creates the
 * business location when a location field is sent (GET /business/my-business
 * needs it) and the submit step requires the business city. Everything else is
 * edited in the existing sections.
 *
 * Validation runs the zod schema directly (`safeParse`): `@hookform/resolvers/zod`
 * does not load under the CRA Jest transform, and this form has three fields.
 */
const CreateBusinessCard: React.FC = () => {
    const { t } = useLanguage();
    const { showToast } = useToast();
    const { setTokens } = useAuthContext();
    const queryClient = useQueryClient();
    const [values, setValues] = useState<CreateBusinessForm>({ businessName: '', country: 'AE', city: '' });
    const [errors, setErrors] = useState<FieldErrors>({});
    const [saving, setSaving] = useState(false);

    const update = (field: keyof CreateBusinessForm) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
            setValues(v => ({ ...v, [field]: e.target.value }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (saving) return;

        const parsed = createBusinessSchema.safeParse(values);
        if (!parsed.success) {
            const next: FieldErrors = {};
            for (const issue of parsed.error.issues) {
                const field = issue.path[0] as keyof CreateBusinessForm;
                if (!next[field]) next[field] = issue.message;
            }
            setErrors(next);
            return;
        }
        setErrors({});
        setSaving(true);
        try {
            try {
                await apiService.saveDraft(parsed.data);
            } catch {
                showToast(t('mybiz.create.failed'), 'error');
                return;
            }
            // The new businessId claim only reaches the token after a refresh.
            // Refresh through the shared single-flight slot; if that joined a
            // refresh which started before the business existed, refresh once more.
            let tokens: { accessToken: string };
            try {
                tokens = await apiService.refreshSession();
                if (readBusinessId(tokens.accessToken) == null) {
                    tokens = await apiService.refreshSession();
                }
            } catch {
                showToast(t('mybiz.create.sessionFailed'), 'error');
                return;
            }
            // The API client already sends the new token. Reload the business
            // first, then update auth state, so the page never sees a businessId
            // claim without the business loaded ("couldn't load" flash).
            await queryClient.invalidateQueries({ queryKey: queryKeys.business.all });
            // Read the refresh token at write time: a silent 401 refresh can rotate
            // it while the business reloads above, and writing back a stale copy
            // would force the owner to sign in again.
            setTokens(tokens.accessToken, apiService.getStoredRefreshToken());
            showToast(t('mybiz.create.success'), 'success');
        } finally {
            setSaving(false);
        }
    };

    return (
        <form className={sectionStyles.card} onSubmit={handleSubmit} noValidate aria-labelledby="create-business-title">
            <h2 id="create-business-title" className={sectionStyles.cardTitle}>{t('mybiz.create.title')}</h2>
            <p className={sectionStyles.cardSub}>{t('mybiz.create.body')}</p>

            <div className={sectionStyles.formGrid}>
                <div className={sectionStyles.field}>
                    <label htmlFor="create-business-name">{t('mybiz.create.name')}</label>
                    <input
                        id="create-business-name"
                        type="text"
                        value={values.businessName}
                        onChange={update('businessName')}
                        maxLength={100}
                        autoComplete="organization"
                        aria-invalid={!!errors.businessName}
                        aria-describedby={errors.businessName ? 'create-business-name-error' : undefined}
                    />
                    {errors.businessName && (
                        <span id="create-business-name-error" role="alert" className={styles.fieldError}>
                            {t(errors.businessName)}
                        </span>
                    )}
                </div>

                <div className={sectionStyles.field}>
                    <label htmlFor="create-business-country">{t('mybiz.create.country')}</label>
                    <select id="create-business-country" value={values.country} onChange={update('country')}>
                        {CREATE_BUSINESS_COUNTRIES.map(code => (
                            <option key={code} value={code}>{t(`mybiz.create.country.${code}`)}</option>
                        ))}
                    </select>
                </div>

                <div className={sectionStyles.field}>
                    <label htmlFor="create-business-city">{t('mybiz.create.city')}</label>
                    <input
                        id="create-business-city"
                        type="text"
                        value={values.city}
                        onChange={update('city')}
                        maxLength={100}
                        autoComplete="address-level2"
                        aria-invalid={!!errors.city}
                        aria-describedby={errors.city ? 'create-business-city-error' : undefined}
                    />
                    {errors.city && (
                        <span id="create-business-city-error" role="alert" className={styles.fieldError}>
                            {t(errors.city)}
                        </span>
                    )}
                </div>
            </div>

            <div className={sectionStyles.saveRow}>
                <span />
                <button type="submit" className={`${sectionStyles.btnPrimary} ${styles.btnDisabled}`} disabled={saving}>
                    {saving ? t('mybiz.create.saving') : t('mybiz.create.action')}
                </button>
            </div>
        </form>
    );
};

export default CreateBusinessCard;
